import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  ActivityIndicator, ScrollView, Modal, Pressable, useWindowDimensions,
} from 'react-native';
import { collection, getDocs, addDoc, serverTimestamp, query, where, Timestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';
import { C, MONTHS, inr, MIcon, RightDrawer, KV, sx, selectStyle } from './PayrollShared';

type Tab = 'partners' | 'daily' | 'ledger';

function toDate(ts: any): Date | null {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const monthKey = (year: number, month: number) => `${year}-${String(month + 1).padStart(2, '0')}`;
function timeOf(ts: any): string {
  const d = toDate(ts);
  return d ? d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}
const num = (v: any) => (typeof v === 'number' ? v : Number(v) || 0);
const surgeOf = (o: any) => num(o.charges?.surge ?? o.surge);
function restaurantPaysOf(o: any): number {
  const promo = o.promo;
  if (!promo) return 0;
  const funded = promo.fundedBy ?? promo.appliedOffer?.fundedBy;
  if (funded !== 'restaurant') return 0;
  let d = 0;
  if (promo.freeDelivery) d += num(promo.freeDeliveryAmount);
  d += num(promo.appliedOffer?.discountAmount ?? promo.discountAmount);
  return d;
}

interface PartnerRow {
  id: string; name: string;
  cashDue: number; orderCount: number; collected: number; pending: number;
  surge: number; restaurantPays: number; orders: any[];
}
interface CollectionDoc {
  id: string; partnerId: string; partnerName: string; date: string; amount: number;
  method?: string; notes?: string; collectedBy?: string; collectedAt?: any;
}
interface CollectTarget {
  id: string; name: string; pending: number; cashDue: number; orderCount: number;
  date: string;       // YYYY-MM-DD the collection is recorded against
  scope: string;      // human label e.g. "March 2025" or "Wed, 12 Mar"
}

export default function CashCollection() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth());
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState<Tab>('partners');
  const [search, setSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('');

  const [partners, setPartners] = useState<Array<{ id: string; name: string }>>([]);
  const [orders, setOrders] = useState<any[]>([]);
  const [collections, setCollections] = useState<CollectionDoc[]>([]);
  const [agentNames, setAgentNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const [detail, setDetail] = useState<PartnerRow | null>(null);
  const [detailScope, setDetailScope] = useState<{ date: string; label: string } | null>(null);
  const [dayDetail, setDayDetail] = useState<number | null>(null);
  const [target, setTarget] = useState<CollectTarget | null>(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Cash');
  const [notes, setNotes] = useState('');
  const [amountError, setAmountError] = useState('');
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  const isCurrentMonth = month === now.getMonth() && year === now.getFullYear();
  const mKey = monthKey(year, month);
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = Timestamp.fromDate(new Date(year, month, 1, 0, 0, 0));
      const end = Timestamp.fromDate(new Date(year, month, daysInMonth, 23, 59, 59, 999));
      const ordersQuery = query(
        collection(db, 'zapee_restaurant_orders'),
        where('createdAt', '>=', start),
        where('createdAt', '<=', end),
      );
      const [partnerSnap, orderSnap, collSnap, shSnap] = await Promise.all([
        getDocs(collection(db, 'zapee_delivery_partners')),
        getDocs(ordersQuery),
        getDocs(collection(db, 'zapee_cash_collections')),
        getDocs(collection(db, 'zapee_share_holders')),
      ]);

      const agentMap: Record<string, string> = {};
      shSnap.docs.forEach(d => { const a: any = d.data(); agentMap[d.id] = a.name || a.contact || a.email || d.id; });
      setAgentNames(agentMap);

      setPartners(partnerSnap.docs.map(d => {
        const data: any = d.data();
        const id = data.partnerId || d.id;
        return { id: String(id), name: String(data.name || data.phone || data.email || id) };
      }));

      const monthOrders = orderSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })).filter((o: any) => {
        if (String(o?.status || '').toUpperCase() !== 'DELIVERED') return false;
        const pm = String(o?.payment?.method || '').toUpperCase();
        if (!(pm.includes('CASH') || pm.includes('COD'))) return false;
        const d = toDate(o?.deliveredAt ?? o?.createdAt);
        return d ? dayKey(d).startsWith(mKey) : false;
      });
      setOrders(monthOrders);

      // Collections for the whole month (match new month-key + legacy date-based docs).
      const monthColls: CollectionDoc[] = collSnap.docs
        .map(d => ({ id: d.id, ...(d.data() as any) }))
        .filter((c: any) => typeof c?.date === 'string' && c.date.startsWith(mKey))
        .map((c: any) => ({
          id: c.id, partnerId: String(c.partnerId || ''), partnerName: String(c.partnerName || ''),
          date: String(c.date || ''), amount: num(c.amount), method: c.method, notes: c.notes,
          collectedBy: c.collectedBy, collectedAt: c.collectedAt,
        }));
      setCollections(monthColls);
    } catch (e: any) {
      console.error('cash collection load failed', e);
      showToast(`Failed to load: ${e?.code || e?.message || 'error'}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [year, month, mKey, daysInMonth]);

  useEffect(() => { load(); }, [load]);

  const changeMonth = (delta: number) => {
    let m = month + delta, y = year;
    if (m < 0) { m = 11; y -= 1; }
    if (m > 11) { m = 0; y += 1; }
    if (y > now.getFullYear() || (y === now.getFullYear() && m > now.getMonth())) return;
    setMonth(m); setYear(y); setPartnerFilter(''); setDetail(null);
  };

  const rows = useMemo<PartnerRow[]>(() => {
    const idToName = new Map<string, string>();
    partners.forEach(p => idToName.set(p.id, p.name));

    const due = new Map<string, { cashDue: number; orderCount: number; surge: number; restPays: number; orders: any[] }>();
    orders.forEach((o: any) => {
      const pid = String(o?.deliveryPartnerId || '');
      if (!pid) return;
      const prev = due.get(pid) || { cashDue: 0, orderCount: 0, surge: 0, restPays: 0, orders: [] };
      prev.cashDue += num(o?.customerPaid);
      prev.orderCount += 1;
      prev.surge += surgeOf(o);
      prev.restPays += restaurantPaysOf(o);
      prev.orders.push(o);
      due.set(pid, prev);
    });

    const collected = new Map<string, number>();
    collections.forEach(c => collected.set(c.partnerId, (collected.get(c.partnerId) || 0) + num(c.amount)));

    const ids = new Set<string>([...due.keys(), ...collected.keys()]);
    const out: PartnerRow[] = [];
    ids.forEach(id => {
      const d = due.get(id) || { cashDue: 0, orderCount: 0, surge: 0, restPays: 0, orders: [] };
      const coll = collected.get(id) || 0;
      if (d.cashDue > 0 || coll > 0) {
        out.push({
          id, name: idToName.get(id) || collections.find(c => c.partnerId === id)?.partnerName || id,
          cashDue: d.cashDue, orderCount: d.orderCount, collected: coll, pending: Math.max(0, d.cashDue - coll),
          surge: d.surge, restaurantPays: d.restPays, orders: d.orders,
        });
      }
    });
    out.sort((a, b) => b.pending - a.pending || b.cashDue - a.cashDue);
    return out;
  }, [partners, orders, collections]);

  const scopedRows = useMemo(() => (partnerFilter ? rows.filter(r => r.id === partnerFilter) : rows), [rows, partnerFilter]);

  const summary = useMemo(() => {
    let cashDue = 0, collected = 0, pending = 0, pendingPartners = 0;
    scopedRows.forEach(r => { cashDue += r.cashDue; collected += r.collected; pending += r.pending; if (r.pending > 0) pendingPartners++; });
    return { cashDue, collected, pending, pendingPartners };
  }, [scopedRows]);

  // Per-day map (scoped to the partner filter): cash due + collected per calendar day.
  const dailyMap = useMemo(() => {
    const due = new Array(daysInMonth + 1).fill(0);
    const got = new Array(daysInMonth + 1).fill(0);
    const ords = partnerFilter ? orders.filter(o => String(o.deliveryPartnerId || '') === partnerFilter) : orders;
    ords.forEach(o => {
      const d = toDate(o.deliveredAt ?? o.createdAt);
      if (d) due[d.getDate()] += num(o.customerPaid);
    });
    const colls = partnerFilter ? collections.filter(c => c.partnerId === partnerFilter) : collections;
    colls.forEach(c => {
      const day = Number(c.date.slice(8, 10));
      if (day >= 1 && day <= daysInMonth) got[day] += c.amount;
    });
    const max = Math.max(1, ...due.slice(1), ...got.slice(1));
    return { due, got, max };
  }, [orders, collections, partnerFilter, daysInMonth]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedRows;
    return scopedRows.filter(r => r.name.toLowerCase().includes(q) || r.id.toLowerCase().includes(q));
  }, [scopedRows, search]);

  // One row per active day in the month (scoped to the partner filter).
  const dailyRows = useMemo(() => {
    const ords = partnerFilter ? orders.filter(o => String(o.deliveryPartnerId || '') === partnerFilter) : orders;
    const colls = partnerFilter ? collections.filter(c => c.partnerId === partnerFilter) : collections;
    const map = new Map<number, { due: number; collected: number; orders: number }>();
    ords.forEach(o => {
      const d = toDate(o.deliveredAt ?? o.createdAt);
      if (!d) return;
      const e = map.get(d.getDate()) || { due: 0, collected: 0, orders: 0 };
      e.due += num(o.customerPaid); e.orders += 1;
      map.set(d.getDate(), e);
    });
    colls.forEach(c => {
      const day = Number(c.date.slice(8, 10));
      if (!day) return;
      const e = map.get(day) || { due: 0, collected: 0, orders: 0 };
      e.collected += c.amount;
      map.set(day, e);
    });
    return Array.from(map.entries())
      .map(([day, v]) => ({ day, ...v, pending: Math.max(0, v.due - v.collected) }))
      .sort((a, b) => b.day - a.day);
  }, [orders, collections, partnerFilter]);

  // Drill-down for a single day: per-partner rows + orders for that day.
  const dayData = useMemo(() => {
    if (dayDetail == null) return null;
    const dayStr = `${mKey}-${String(dayDetail).padStart(2, '0')}`;
    const ords = (partnerFilter ? orders.filter(o => String(o.deliveryPartnerId || '') === partnerFilter) : orders)
      .filter(o => { const d = toDate(o.deliveredAt ?? o.createdAt); return !!d && d.getDate() === dayDetail; });
    const colls = (partnerFilter ? collections.filter(c => c.partnerId === partnerFilter) : collections)
      .filter(c => Number(c.date.slice(8, 10)) === dayDetail);

    const idToName = new Map(partners.map(p => [p.id, p.name] as [string, string]));
    const pmap = new Map<string, { cashDue: number; orders: number }>();
    ords.forEach(o => {
      const pid = String(o.deliveryPartnerId || '');
      if (!pid) return;
      const e = pmap.get(pid) || { cashDue: 0, orders: 0 };
      e.cashDue += num(o.customerPaid); e.orders += 1;
      pmap.set(pid, e);
    });
    const collByPartner = new Map<string, number>();
    colls.forEach(c => collByPartner.set(c.partnerId, (collByPartner.get(c.partnerId) || 0) + c.amount));

    const ids = new Set<string>([...pmap.keys(), ...collByPartner.keys()]);
    const partnerRows = Array.from(ids).map(id => {
      const e = pmap.get(id) || { cashDue: 0, orders: 0 };
      const coll = collByPartner.get(id) || 0;
      return { id, name: idToName.get(id) || id, cashDue: e.cashDue, orders: e.orders, collected: coll, pending: Math.max(0, e.cashDue - coll) };
    }).sort((a, b) => b.pending - a.pending || b.cashDue - a.cashDue);

    const cashDue = ords.reduce((s, o) => s + num(o.customerPaid), 0);
    const collected = colls.reduce((s, c) => s + c.amount, 0);
    let product = 0, delivery = 0, packing = 0, platform = 0, surge = 0, restPays = 0;
    ords.forEach((o: any) => {
      const ch = o.charges || {};
      const d = num(ch.deliveryCharge ?? o.deliveryCharge);
      const p = num(ch.packing ?? o.packing);
      const pf = num(ch.platformFee ?? o.platformFee);
      const tax = num(ch.tax ?? o.tax);
      const s = surgeOf(o);
      const grand = num(o.totalPrice ?? o.grandTotal ?? o.total);
      product += Math.max(0, grand - (d + p + pf + s + tax));
      delivery += d; packing += p; platform += pf; surge += s; restPays += restaurantPaysOf(o);
    });
    const label = new Date(year, month, dayDetail).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' });
    return { day: dayDetail, dayStr, label, ords, partnerRows, cashDue, collected, pending: Math.max(0, cashDue - collected), product, delivery, packing, platform, surge, restPays };
  }, [dayDetail, orders, collections, partners, partnerFilter, mKey, year, month]);

  const sortedCollections = useMemo(() => {
    let list = partnerFilter ? collections.filter(c => c.partnerId === partnerFilter) : collections;
    list = [...list].sort((a, b) => (toDate(b.collectedAt)?.getTime() || 0) - (toDate(a.collectedAt)?.getTime() || 0));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(c => (c.partnerName || '').toLowerCase().includes(q) || (c.partnerId || '').toLowerCase().includes(q));
  }, [collections, search, partnerFilter]);

  const detailFees = useMemo(() => {
    let product = 0, delivery = 0, packing = 0, platform = 0;
    (detail?.orders ?? []).forEach((o: any) => {
      const ch = o.charges || {};
      const d = num(ch.deliveryCharge ?? o.deliveryCharge);
      const p = num(ch.packing ?? o.packing);
      const pf = num(ch.platformFee ?? o.platformFee);
      const tax = num(ch.tax ?? o.tax);
      const s = surgeOf(o);
      const grand = num(o.totalPrice ?? o.grandTotal ?? o.total);
      product += Math.max(0, grand - (d + p + pf + s + tax));
      delivery += d; packing += p; platform += pf;
    });
    return { product, delivery, packing, platform };
  }, [detail]);

  const startCollect = (t: CollectTarget) => {
    setTarget(t); setAmount(String(Math.round(t.pending))); setMethod('Cash'); setNotes(''); setAmountError('');
  };

  // Open the partner detail drawer scoped to the whole month (Partners tab).
  const openPartnerMonth = (r: PartnerRow) => {
    const recordDate = isCurrentMonth ? dayKey(now) : dayKey(new Date(year, month, daysInMonth));
    setDetail(r);
    setDetailScope({ date: recordDate, label: `${MONTHS[month]} ${year}` });
  };

  // Open the SAME drawer from a day — but scoped to that day's data.
  const openPartnerFromDay = (id: string) => {
    if (dayDetail == null || !dayData) return;
    const row = dayData.partnerRows.find(p => p.id === id);
    if (!row) return;
    const pOrders = dayData.ords.filter((o: any) => String(o.deliveryPartnerId || '') === id);
    let surge = 0, restPays = 0;
    pOrders.forEach((o: any) => { surge += surgeOf(o); restPays += restaurantPaysOf(o); });
    setDayDetail(null);
    setDetail({ id: row.id, name: row.name, cashDue: row.cashDue, orderCount: row.orders, collected: row.collected, pending: row.pending, surge, restaurantPays: restPays, orders: pOrders });
    setDetailScope({ date: dayData.dayStr, label: dayData.label });
  };

  // Collect for whoever's drawer is open, recorded against its scope's date.
  const collectDetail = () => {
    if (!detail || !detailScope) return;
    startCollect({ id: detail.id, name: detail.name, pending: detail.pending, cashDue: detail.cashDue, orderCount: detail.orderCount, date: detailScope.date, scope: detailScope.label });
  };

  const submitCollect = async () => {
    if (!target) return;
    const amt = Number(amount);
    if (!amount.trim() || isNaN(amt) || amt <= 0) { setAmountError('Enter an amount greater than 0'); return; }
    setSaving(true);
    try {
      await addDoc(collection(db, 'zapee_cash_collections'), {
        partnerId: target.id, partnerName: target.name, date: target.date, periodMonth: month, periodYear: year,
        amount: amt, cashDue: target.cashDue, orderCount: target.orderCount, collectedAt: serverTimestamp(),
        collectedBy: auth.currentUser?.uid || '', method, notes: notes.trim(),
      });
      setTarget(null); setDetail(null); setDayDetail(null);
      showToast('Cash collection recorded');
      await load();
    } catch (e) {
      setAmountError('Failed to record collection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const Th = ({ label, flex, align }: { label: string; flex: number; align?: 'center' | 'right' }) => (
    <View style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}>
      <Text style={tb.thTxt}>{label}</Text>
    </View>
  );

  const renderPartners = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Partner" flex={2.2} />
        <Th label="Orders" flex={0.9} align="center" />
        <Th label="Cash Due" flex={1.2} align="right" />
        <Th label="Surge" flex={1} align="right" />
        <Th label="Collected" flex={1.2} align="right" />
        <Th label="Pending" flex={1.2} align="right" />
        <Th label="" flex={1.1} align="right" />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {filteredRows.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No cash to collect this month</Text></View>
        ) : filteredRows.map((r, i) => {
          const done = r.pending <= 0;
          return (
            <TouchableOpacity key={r.id} style={[tb.row, i % 2 === 1 && tb.rowAlt]} onPress={() => openPartnerMonth(r)}>
              <View style={[tb.td, { flex: 2.2 }]}>
                <Text style={tb.name} numberOfLines={1}>{r.name}</Text>
                <Text style={tb.sub} numberOfLines={1}>{r.id}</Text>
              </View>
              <Text style={[tb.cell, { flex: 0.9, textAlign: 'center' }]}>{r.orderCount}</Text>
              <Text style={[tb.cell, { flex: 1.2, textAlign: 'right', fontWeight: '700' }]}>{inr(r.cashDue)}</Text>
              <Text style={[tb.cell, { flex: 1, textAlign: 'right', color: C.accent }]}>{r.surge ? inr(r.surge) : '—'}</Text>
              <Text style={[tb.cell, { flex: 1.2, textAlign: 'right', color: C.accent, fontWeight: '700' }]}>{inr(r.collected)}</Text>
              <Text style={[tb.cell, { flex: 1.2, textAlign: 'right', color: r.pending > 0 ? C.danger : C.muted, fontWeight: '700' }]}>{inr(r.pending)}</Text>
              <View style={[tb.td, { flex: 1.1, alignItems: 'flex-end' }]}>
                {done ? (
                  <View style={tb.doneBadge}><Text style={tb.doneTxt}>✓ Collected</Text></View>
                ) : (
                  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                    <Text style={sx.tapPayTxt}>Collect</Text><MIcon name="chevron_right" size={18} color={C.accent} />
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );

  const renderLedger = () => (
    <View style={tb.table}>
      <View style={tb.headerRow}>
        <Th label="Partner" flex={2.4} />
        <Th label="Amount" flex={1.2} align="right" />
        <Th label="Method" flex={1.2} />
        <Th label="Collected By" flex={1.8} />
        <Th label="Date / Time" flex={1.5} />
      </View>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
        {sortedCollections.length === 0 ? (
          <View style={sx.empty}><Text style={sx.emptyTxt}>No collections this month</Text></View>
        ) : sortedCollections.map((c, i) => (
          <View key={c.id} style={[tb.row, i % 2 === 1 && tb.rowAlt]}>
            <View style={[tb.td, { flex: 2.4 }]}>
              <Text style={tb.name} numberOfLines={1}>{c.partnerName || c.partnerId}</Text>
              <Text style={tb.sub} numberOfLines={1}>{c.partnerId}</Text>
            </View>
            <Text style={[tb.cell, { flex: 1.2, textAlign: 'right', color: C.accent, fontWeight: '700' }]}>{inr(c.amount)}</Text>
            <Text style={[tb.cell, { flex: 1.2 }]} numberOfLines={1}>{c.method || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.8 }]} numberOfLines={1}>{(c.collectedBy && agentNames[c.collectedBy]) || c.collectedBy || '—'}</Text>
            <Text style={[tb.cell, { flex: 1.5 }]} numberOfLines={1}>{timeOf(c.collectedAt)}</Text>
          </View>
        ))}
      </ScrollView>
    </View>
  );

  const renderDaily = () => {
    const wd = (day: number) => new Date(year, month, day).toLocaleDateString('en-IN', { weekday: 'short' });
    return (
      <View style={tb.table}>
        <View style={tb.headerRow}>
          <Th label="Date" flex={2.2} />
          <Th label="Orders" flex={1} align="center" />
          <Th label="Cash Due" flex={1.3} align="right" />
          <Th label="Collected" flex={1.3} align="right" />
          <Th label="Pending" flex={1.3} align="right" />
        </View>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
          {dailyRows.length === 0 ? (
            <View style={sx.empty}><Text style={sx.emptyTxt}>No daily activity this month</Text></View>
          ) : dailyRows.map((r, i) => {
            const today = isCurrentMonth && r.day === now.getDate();
            return (
              <TouchableOpacity key={r.day} style={[tb.row, i % 2 === 1 && tb.rowAlt]} onPress={() => setDayDetail(r.day)}>
                <View style={[tb.td, { flex: 2.2, flexDirection: 'row', alignItems: 'center' }]}>
                  <View style={{ flex: 1 }}>
                    <Text style={[tb.name, today && { color: C.accent }]}>{wd(r.day)}</Text>
                    <Text style={[tb.sub, today && { color: C.accent }]}>{r.day} {MONTHS[month].slice(0, 3)}</Text>
                  </View>
                  <MIcon name="chevron_right" size={16} color={C.muted} style={{ marginLeft: 4 }} />
                </View>
                <Text style={[tb.cell, { flex: 1, textAlign: 'center' }]}>{r.orders}</Text>
                <Text style={[tb.cell, { flex: 1.3, textAlign: 'right', fontWeight: '700' }]}>{inr(r.due)}</Text>
                <Text style={[tb.cell, { flex: 1.3, textAlign: 'right', color: C.accent, fontWeight: '700' }]}>{inr(r.collected)}</Text>
                <Text style={[tb.cell, { flex: 1.3, textAlign: 'right', color: r.pending > 0 ? C.danger : C.muted, fontWeight: '700' }]}>{inr(r.pending)}</Text>
              </TouchableOpacity>
            );
          })}
        </ScrollView>
      </View>
    );
  };

  const partnerOptions = useMemo(() => {
    const seen = new Map<string, string>();
    rows.forEach(r => seen.set(r.id, r.name));
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  return (
    <View style={sx.screen}>
      <View style={sx.monthRow}>
        <TouchableOpacity style={sx.monthBtn} onPress={() => changeMonth(-1)}><Text style={sx.monthBtnTxt}>‹</Text></TouchableOpacity>
        <View style={sx.monthLabelWrap}><Text style={sx.monthLabel}>{MONTHS[month]} {year}</Text></View>
        <TouchableOpacity style={[sx.monthBtn, isCurrentMonth && sx.monthBtnDisabled]} onPress={() => changeMonth(1)} disabled={isCurrentMonth}>
          <Text style={[sx.monthBtnTxt, isCurrentMonth && sx.monthBtnTxtDisabled]}>›</Text>
        </TouchableOpacity>
      </View>

      <View style={sx.summaryCard}>
        <View style={[sx.summaryRow, isMobile && { flexWrap: 'wrap', gap: 16 }]}>
          <View style={sx.summaryItem}><Text style={sx.summaryLabel}>Total Cash Due</Text><Text style={sx.summaryValueBig}>{inr(summary.cashDue)}</Text><Text style={sx.summaryCaption}>this month</Text></View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}><Text style={sx.summaryLabel}>Collected</Text><Text style={sx.summaryValue}>{inr(summary.collected)}</Text><Text style={sx.summaryCaption}>received</Text></View>
          <View style={sx.summaryDivider} />
          <View style={sx.summaryItem}><Text style={sx.summaryLabel}>Pending</Text><Text style={sx.summaryValue}>{inr(summary.pending)}</Text><Text style={sx.summaryCaption}>{summary.pendingPartners} partners</Text></View>
        </View>
      </View>

      {/* Daily collection map */}
      <View style={dm.card}>
        <Text style={sx.sectionLabel}>DAILY MAP {partnerFilter ? `· ${partnerOptions.find(p => p[0] === partnerFilter)?.[1] ?? ''}` : '· ALL PARTNERS'}</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ marginTop: 6 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 5, height: 84, paddingTop: 6 }}>
            {Array.from({ length: daysInMonth }, (_, idx) => {
              const day = idx + 1;
              const due = dailyMap.due[day], got = dailyMap.got[day];
              const dueH = (due / dailyMap.max) * 54;
              const gotH = (got / dailyMap.max) * 54;
              const isToday = isCurrentMonth && day === now.getDate();
              return (
                <View key={day} style={{ alignItems: 'center', width: 16 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 2, height: 56 }}>
                    <View style={{ width: 5, height: Math.max(due > 0 ? 3 : 0, dueH), backgroundColor: C.border, borderRadius: 2 }} />
                    <View style={{ width: 5, height: Math.max(got > 0 ? 3 : 0, gotH), backgroundColor: C.accent, borderRadius: 2 }} />
                  </View>
                  <Text style={{ fontSize: 8, color: isToday ? C.accent : C.muted, fontWeight: isToday ? '900' : '600', marginTop: 3 }}>{day}</Text>
                </View>
              );
            })}
          </View>
        </ScrollView>
        <View style={{ flexDirection: 'row', gap: 14, marginTop: 8 }}>
          <Legend color={C.border} label="Cash due" />
          <Legend color={C.accent} label="Collected" />
        </View>
      </View>

      <View style={[sx.controls, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
        <View style={sx.tabRow}>
          {([['partners', 'Partners'], ['daily', 'Daily'], ['ledger', 'Ledger']] as [Tab, string][]).map(([t, lbl]) => (
            <TouchableOpacity key={t} style={[sx.tab, tab === t && sx.tabActive]} onPress={() => setTab(t)}>
              <Text style={[sx.tabTxt, tab === t && sx.tabTxtActive]}>{lbl}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <select value={partnerFilter} onChange={(e: any) => setPartnerFilter(e.target.value)} style={{ ...selectStyle, width: 200, height: 44, marginBottom: 0 }}>
          <option value="">All partners</option>
          {partnerOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <View style={[sx.searchBox, { flex: 1 }]}>
          <MIcon name="search" size={16} color={C.muted} style={{ marginRight: 8 }} />
          <TextInput style={sx.searchInput} placeholder="Search by name or id…" value={search} onChangeText={setSearch} placeholderTextColor={C.muted} />
        </View>
        <TouchableOpacity style={sx.refreshBtn} onPress={load}><MIcon name="refresh" size={20} color={C.text} /></TouchableOpacity>
      </View>

      {loading ? (
        <View style={sx.loading}><ActivityIndicator size="large" color={C.teal} /><Text style={sx.loadingTxt}>Loading cash collection…</Text></View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: 980, flexGrow: 1 }}>
          {tab === 'partners' ? renderPartners() : tab === 'daily' ? renderDaily() : renderLedger()}
        </ScrollView>
      )}

      {/* Details drawer */}
      <RightDrawer visible={!!detail} onClose={() => setDetail(null)} title={detail?.name || ''} subtitle={`${detailScope?.label ?? ''} · Cash collection`}>
        {detail && (
          <>
            <View style={sx.drawerCard}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>Cash Due (to company)</Text>
                <Text style={{ fontSize: 22, fontWeight: '900', color: C.text }}>{inr(detail.cashDue)}</Text>
              </View>
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Cash Orders" value={`${detail.orderCount}`} />
              <Text style={[sx.sectionLabel, { marginTop: 8, marginBottom: 4 }]}>FEE BREAKDOWN</Text>
              <KV label="Product Amount" value={inr(detailFees.product)} />
              <KV label="Delivery Charges" value={inr(detailFees.delivery)} />
              {detailFees.packing > 0 && <KV label="Packing" value={inr(detailFees.packing)} />}
              <KV label="Platform Fee" value={inr(detailFees.platform)} />
              <KV label="Surge (partner earns)" value={inr(detail.surge)} color={C.accent} />
              <KV label="Restaurant pays (free del / offers)" value={inr(detail.restaurantPays)} color={C.teal} />
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Collected" value={inr(detail.collected)} color={C.accent} />
              <KV label="Pending" value={inr(detail.pending)} color={detail.pending > 0 ? C.danger : C.muted} bold />
            </View>

            {detail.pending > 0 && (
              <TouchableOpacity style={[sx.fullBtn, { marginBottom: 14 }]} onPress={collectDetail}>
                <Text style={sx.fullTxt}>Collect {inr(detail.pending)}</Text>
              </TouchableOpacity>
            )}

            <Text style={sx.sectionLabel}>ORDER BREAKDOWN · {detail.orders.length} ORDER{detail.orders.length === 1 ? '' : 'S'}</Text>
            {[...detail.orders].sort((a, b) => (toDate(b.deliveredAt ?? b.createdAt)?.getTime() || 0) - (toDate(a.deliveredAt ?? a.createdAt)?.getTime() || 0)).map((o: any) => <OrderCard key={o.id} o={o} />)}
          </>
        )}
      </RightDrawer>

      {/* Day drill-down drawer */}
      <RightDrawer visible={dayDetail != null} onClose={() => setDayDetail(null)} title={dayData?.label || ''} subtitle={`${MONTHS[month]} ${year} · Daily collection`}>
        {dayData && (
          <>
            <View style={sx.drawerCard}>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>Cash Due (this day)</Text>
                <Text style={{ fontSize: 22, fontWeight: '900', color: C.text }}>{inr(dayData.cashDue)}</Text>
              </View>
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Cash Orders" value={`${dayData.ords.length}`} />
              <Text style={[sx.sectionLabel, { marginTop: 8, marginBottom: 4 }]}>FEE BREAKDOWN</Text>
              <KV label="Product Amount" value={inr(dayData.product)} />
              <KV label="Delivery Charges" value={inr(dayData.delivery)} />
              {dayData.packing > 0 && <KV label="Packing" value={inr(dayData.packing)} />}
              <KV label="Platform Fee" value={inr(dayData.platform)} />
              <KV label="Surge (partner earns)" value={inr(dayData.surge)} color={C.accent} />
              <KV label="Restaurant pays (free del / offers)" value={inr(dayData.restPays)} color={C.teal} />
              <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
              <KV label="Collected" value={inr(dayData.collected)} color={C.accent} />
              <KV label="Pending" value={inr(dayData.pending)} color={dayData.pending > 0 ? C.danger : C.muted} bold />
            </View>

            <Text style={sx.sectionLabel}>PARTNERS · {dayData.partnerRows.length}</Text>
            {dayData.partnerRows.map(p => (
              <View key={p.id} style={sx.orderCard}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                  <TouchableOpacity style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }} onPress={() => openPartnerFromDay(p.id)}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13.5, fontWeight: '800', color: C.text }} numberOfLines={1}>{p.name}</Text>
                      <Text style={{ fontSize: 10.5, color: C.muted, marginTop: 1 }}>{p.orders} order{p.orders === 1 ? '' : 's'} · tap for details</Text>
                    </View>
                    <MIcon name="chevron_right" size={16} color={C.muted} style={{ marginRight: 6 }} />
                  </TouchableOpacity>
                  {p.pending > 0 ? (
                    <TouchableOpacity
                      style={dd.collectBtn}
                      onPress={() => startCollect({ id: p.id, name: p.name, pending: p.pending, cashDue: p.cashDue, orderCount: p.orders, date: dayData.dayStr, scope: dayData.label })}
                    >
                      <Text style={dd.collectTxt}>Collect {inr(p.pending)}</Text>
                    </TouchableOpacity>
                  ) : (
                    <View style={tb.doneBadge}><Text style={tb.doneTxt}>✓ Collected</Text></View>
                  )}
                </View>
                <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
                <Mini label="Cash Due" value={inr(p.cashDue)} color={C.text} />
                <Mini label="Collected" value={inr(p.collected)} color={C.accent} />
                <Mini label="Pending" value={inr(p.pending)} color={p.pending > 0 ? C.danger : C.muted} />
              </View>
            ))}

            <Text style={[sx.sectionLabel, { marginTop: 6 }]}>ORDER BREAKDOWN · {dayData.ords.length} ORDER{dayData.ords.length === 1 ? '' : 'S'}</Text>
            {[...dayData.ords].sort((a, b) => (toDate(b.deliveredAt ?? b.createdAt)?.getTime() || 0) - (toDate(a.deliveredAt ?? a.createdAt)?.getTime() || 0)).map((o: any) => <OrderCard key={o.id} o={o} />)}
          </>
        )}
      </RightDrawer>

      {/* Collect modal */}
      <Modal visible={!!target} transparent animationType="fade" onRequestClose={() => !saving && setTarget(null)}>
        <Pressable style={pm.overlay} onPress={() => !saving && setTarget(null)}>
          <Pressable style={pm.card} onPress={() => {}}>
            <Text style={pm.title}>Collect Cash</Text>
            {target && <Text style={pm.subtitle}>{target.name} · Pending {inr(target.pending)} · {target.scope}</Text>}
            <Text style={pm.label}>Amount (₹)</Text>
            <TextInput style={[pm.input, amountError && pm.inputError]} value={amount} onChangeText={(v) => { setAmount(v.replace(/[^0-9]/g, '')); if (amountError) setAmountError(''); }} placeholder="0" placeholderTextColor={C.muted} keyboardType="numeric" />
            <Text style={pm.label}>Payment Method</Text>
            <select value={method} onChange={(e: any) => setMethod(e.target.value)} style={selectStyle}>
              <option value="Cash">Cash</option><option value="UPI">UPI</option><option value="Bank">Bank</option>
            </select>
            <Text style={pm.label}>Notes (optional)</Text>
            <TextInput style={[pm.input, { height: 60 }]} value={notes} onChangeText={setNotes} placeholder="Any remarks…" placeholderTextColor={C.muted} multiline />
            {amountError ? <Text style={pm.errorTxt}>{amountError}</Text> : null}
            <View style={pm.actions}>
              <TouchableOpacity style={pm.cancelBtn} onPress={() => setTarget(null)} disabled={saving}><Text style={pm.cancelTxt}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={[pm.confirmBtn, saving && { opacity: 0.6 }]} onPress={submitCollect} disabled={saving}>
                {saving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={pm.confirmTxt}>Confirm</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {toast && (
        <View style={[styles.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.accent }]}>
          <Text style={styles.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

const Legend = ({ color, label }: { color: string; label: string }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
    <View style={{ width: 10, height: 10, borderRadius: 3, backgroundColor: color }} />
    <Text style={{ fontSize: 10.5, color: C.muted, fontWeight: '600' }}>{label}</Text>
  </View>
);

const Mini = ({ label, value, color }: { label: string; value: string; color: string }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 1.5 }}>
    <Text style={{ fontSize: 11, color: C.muted, fontWeight: '600' }}>{label}</Text>
    <Text style={{ fontSize: 11, fontWeight: '800', color }}>{value}</Text>
  </View>
);

function OrderCard({ o }: { o: any }) {
  const ch = o.charges || {};
  const delivery = num(ch.deliveryCharge ?? o.deliveryCharge);
  const packing = num(ch.packing ?? o.packing);
  const platform = num(ch.platformFee ?? o.platformFee);
  const tax = num(ch.tax ?? o.tax);
  const surge = surgeOf(o);
  const restPays = restaurantPaysOf(o);
  const grand = num(o.totalPrice ?? o.grandTotal ?? o.total);
  const product = Math.max(0, grand - (delivery + packing + platform + surge + tax));
  const paid = num(o.customerPaid);
  return (
    <View style={sx.orderCard}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
        <Text style={{ fontSize: 13, fontWeight: '800', color: C.text }}>#{String(o.id).slice(0, 6).toUpperCase()}</Text>
        <Text style={{ fontSize: 13.5, fontWeight: '900', color: C.text }}>Collect {inr(paid)}</Text>
      </View>
      <Text style={{ fontSize: 10.5, color: C.muted, marginTop: 2 }}>{timeOf(o.deliveredAt ?? o.createdAt)}</Text>
      <View style={{ height: 1, backgroundColor: C.border, marginVertical: 8 }} />
      <Mini label="Product Amount" value={inr(product)} color={C.text} />
      <Mini label="Delivery Charge" value={inr(delivery)} color={C.muted} />
      {packing > 0 && <Mini label="Packing" value={inr(packing)} color={C.muted} />}
      <Mini label="Platform Fee" value={inr(platform)} color={C.muted} />
      {tax > 0 && <Mini label="Tax" value={inr(tax)} color={C.muted} />}
      {surge > 0 && <Mini label="Surge (partner earns)" value={inr(surge)} color={C.accent} />}
      {restPays > 0 && <Mini label="Restaurant pays (free del/offers)" value={inr(restPays)} color={C.teal} />}
      <View style={{ height: 1, backgroundColor: C.border, marginVertical: 6 }} />
      <Mini label="Customer Paid (cash)" value={inr(paid)} color={C.text} />
    </View>
  );
}

const dd = StyleSheet.create({
  collectBtn: { backgroundColor: C.accent, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 8 },
  collectTxt: { color: '#fff', fontSize: 12, fontWeight: '800' },
});

const styles = StyleSheet.create({
  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

const dm = StyleSheet.create({
  card: { backgroundColor: '#fff', borderRadius: 14, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 14 },
});

const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#fff', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#f1f6f8', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, textTransform: 'uppercase' as const },
  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  rowAlt: { backgroundColor: '#fbfcfe' },
  td: { justifyContent: 'center', paddingRight: 8 },
  cell: { fontSize: 13, color: C.text, paddingRight: 8 },
  name: { fontSize: 14, fontWeight: '600', color: C.text },
  sub: { fontSize: 10, color: C.muted, marginTop: 1 },
  doneBadge: { backgroundColor: '#DCFCE7', borderRadius: 12, paddingHorizontal: 10, paddingVertical: 4 },
  doneTxt: { color: C.accent, fontSize: 11, fontWeight: '800' },
});

const pm = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 420 },
  title: { fontSize: 18, fontWeight: '800', color: C.text, marginBottom: 4 },
  subtitle: { fontSize: 13, color: C.muted, marginBottom: 8 },
  label: { fontSize: 12, fontWeight: '700', color: C.text, marginBottom: 6, marginTop: 10 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1.5, borderColor: C.border, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: C.text, outlineStyle: 'none' } as any,
  inputError: { borderColor: C.danger },
  errorTxt: { fontSize: 12, color: C.danger, marginTop: 8 },
  actions: { flexDirection: 'row', gap: 10, marginTop: 20 },
  cancelBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.text, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 12, borderRadius: 10, backgroundColor: C.accent, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});
